import Uppy from '@uppy/core';
import AwsS3 from '@uppy/aws-s3';
import type { R2UploadsApi } from './types';
import { getStandardizedFileName } from './filenameUtils';
import { extractUploadErrorDiagnostics } from './loggingUtils';
import type { UploadDiagnosticContext } from './loggingUtils';

type UploadProgressHandler = (
  percentage: number,
  bytesUploaded: number
) => void;

type UploadParams = {
  correlationId: string;
  file: File;
  onProgress?: UploadProgressHandler;
  r2UploadsApi: R2UploadsApi;
};

type UppyUploadResult = {
  assetUrl: string;
  fileName: string;
};

const DEFAULT_PART_SIZE = 8 * 1024 * 1024; // 8 MiB

const toMultipartParts = (
  parts: Array<{ PartNumber?: number; ETag?: string }>
) =>
  parts
    .filter((part) => part.PartNumber != null && part.ETag != null)
    .map((part) => ({
      partNumber: part.PartNumber as number,
      eTag: part.ETag as string,
    }));

const ensurePresignedPart = (
  partNumber: number,
  parts?: Array<{
    partNumber: number;
    presignedUrl: string;
    headers?: Record<string, string>;
  }>
) => {
  if (!parts || !parts.length) {
    throw new Error(`No presigned URLs returned for part ${partNumber}`);
  }
  const match =
    parts.find((candidate) => candidate.partNumber === partNumber) ?? parts[0];
  if (!match?.presignedUrl) {
    throw new Error(`Missing presigned URL for part ${partNumber}`);
  }
  return match;
};

export const createAwsS3Uploader = ({
  correlationId,
  r2UploadsApi,
  onPresignedUrlUsed,
  onUploadError,
}: {
  correlationId: string;
  r2UploadsApi: R2UploadsApi;
  onPresignedUrlUsed?: (url: string) => void;
  onUploadError?: (step: string, error: unknown) => void;
}) => {
  const uppy = new Uppy({
    allowMultipleUploads: false,
    autoProceed: false,
  });

  // Capture the boundary that fails, even when multipart callbacks overlap.
  const atStep = async <T>(
    step: string,
    callback: () => Promise<T>
  ): Promise<T> => {
    try {
      return await callback();
    } catch (error) {
      onUploadError?.(step, error);
      throw error;
    }
  };

  uppy.use(AwsS3, {
    limit: 2,
    shouldUseMultipart: (file) => {
      // Use multipart for files larger than 50MB
      const MULTIPART_THRESHOLD = 50 * 1024 * 1024; // 50 MB
      return (file.size ?? 0) > MULTIPART_THRESHOLD;
    },
    getUploadParameters: async (file) =>
      atStep('signing', async () => {
        const standardizedFileName = getStandardizedFileName(file.name);
        const response = await r2UploadsApi.getPresignedUrl({
          correlationId,
          fileName: standardizedFileName,
          contentType: file.type,
        });
        const payload = response.payload;

        if (!payload.presignedUrl) {
          throw new Error('Missing presigned URL from response');
        }

        // Track the presigned URL for error logging
        onPresignedUrlUsed?.(payload.presignedUrl);

        // Return the presigned URL for direct upload
        // Include headers that are in the presigned URL signature
        const MAX_AGE_ONE_YEAR = 1 * 365 * 24 * 60 * 60; // 1 year in seconds
        return {
          method: 'PUT',
          url: payload.presignedUrl,
          fields: {},
          headers: {
            'Content-Type': file.type,
            'Cache-Control': `public, max-age=${MAX_AGE_ONE_YEAR}`,
            'x-amz-meta-correlation-id': correlationId,
          },
        };
      }),
    // Multipart upload configuration
    getChunkSize: () => DEFAULT_PART_SIZE,
    createMultipartUpload: async (file) =>
      atStep('multipart-initiate', async () => {
        const standardizedFileName = getStandardizedFileName(file.name);
        const response = await r2UploadsApi.initiateMultipart({
          fileName: standardizedFileName,
          contentType: file.type,
          metadata: {
            size: String(file.size ?? 0),
            lastModified: String(
              (file.data as File)?.lastModified ?? Date.now()
            ),
          },
        });
        const payload = response.payload;
        if (!payload.objectKey) {
          throw new Error('Missing objectKey from multipart initiate response');
        }
        return { uploadId: payload.uploadId, key: payload.objectKey };
      }),
    listParts: async (_file, _opts) => [],
    signPart: async (_file, opts) =>
      atStep('multipart-sign-part', async () => {
        const response = await r2UploadsApi.getMultipartPresignedUrls({
          correlationId,
          uploadId: opts.uploadId,
          objectKey: opts.key,
          partNumber: opts.partNumber,
        });
        const { presignedUrl, headers } = ensurePresignedPart(
          opts.partNumber,
          response.payload.parts
        );

        // Track the presigned URL for error logging
        onPresignedUrlUsed?.(presignedUrl);

        return {
          method: 'PUT',
          url: presignedUrl,
          headers: {
            ...headers,
            'x-amz-meta-correlation-id': correlationId,
          },
        };
      }),
    abortMultipartUpload: async (_file, opts) => {
      await r2UploadsApi.abortMultipart(opts.uploadId, opts.key);
    },
    completeMultipartUpload: async (_file, opts) =>
      atStep('multipart-complete', async () => {
        const response = await r2UploadsApi.completeMultipart({
          uploadId: opts.uploadId,
          objectKey: opts.key,
          parts: toMultipartParts(opts.parts),
        });
        return { location: response.payload.assetUrl };
      }),
  });

  return uppy;
};

export const uploadWithAwsS3 = async ({
  correlationId,
  file,
  onProgress,
  r2UploadsApi,
}: UploadParams): Promise<UppyUploadResult> => {
  const uploadContext: UploadDiagnosticContext = {
    bytesUploaded: 0,
    hadUploadProgress: false,
  };
  const failureSteps = new Map<unknown, string>();
  let actualFileId: string | null = null;

  const uppy = createAwsS3Uploader({
    correlationId,
    r2UploadsApi,
    onPresignedUrlUsed: (url) => {
      uploadContext.presignedUrl = url;
    },
    onUploadError: (step, error) => {
      failureSteps.set(error, step);
    },
  });

  return new Promise<UppyUploadResult>((resolve, reject) => {
    const cleanup = () => {
      uppy.close();
    };

    uppy.on('upload-progress', (uppyFile, progress) => {
      if (actualFileId && uppyFile.id !== actualFileId) return;
      const { bytesUploaded, bytesTotal, percentage } = progress;
      uploadContext.bytesUploaded = bytesUploaded;
      uploadContext.hadUploadProgress = true;
      const pct = bytesTotal
        ? Math.round((bytesUploaded / bytesTotal) * 100)
        : Math.round(percentage ?? 0);
      onProgress?.(pct, bytesUploaded);
    });

    const handleError = (
      error: unknown,
      original = false,
      response?: unknown
    ) => {
      // Core emits a generic error first. Both callbacks share this context,
      // so the later original error enriches the already-rejected Error.
      try {
        Object.assign(
          uploadContext,
          extractUploadErrorDiagnostics(error, response)
        );
        uploadContext.uploadStep =
          failureSteps.get(error) ??
          (original ? 'storage-upload' : uploadContext.uploadStep ?? 'unknown');
      } catch {}
      const enhancedError =
        error instanceof Error ? error : new Error(String(error));
      (enhancedError as any).uploadContext = uploadContext;
      cleanup();
      reject(enhancedError);
    };

    uppy.on('upload-error', (_file, error, response) => {
      handleError(error, true, response);
    });
    uppy.on('error', (error) => {
      handleError(error);
    });

    uppy.on('upload-success', (uppyFile, response) => {
      // Capture the actual file ID that Uppy generated
      actualFileId = uppyFile.id;
      const uploadURL =
        (response as { uploadURL?: string } | undefined)?.uploadURL ??
        (uppyFile as any).uploadURL;
      const location =
        (response as { location?: string } | undefined)?.location ?? uploadURL;

      if (!location) {
        cleanup();
        reject(new Error('Upload completed without a location URL'));
        return;
      }
      cleanup();

      resolve({
        assetUrl: location,
        fileName: getStandardizedFileName(file.name),
      });
    });

    (async () => {
      try {
        const addedFileId = await uppy.addFile({
          name: file.name,
          type: file.type,
          data: file,
          size: file.size,
          meta: { correlationId },
        });
        actualFileId = addedFileId;

        await uppy.upload();
      } catch (error) {
        handleError(error as Error);
      }
    })();
  });
};

// Keep the old function name for backward compatibility
export const uploadWithAwsS3Multipart = uploadWithAwsS3;
